import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Bot } from "lucide-react";
import { sendChat, fetchChatbotConfig } from "../services/api";

const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [welcome, setWelcome] = useState(
    "Hi! Welcome to Best For Medals \uD83D\uDC4B How can I help?"
  );
  const [quickReplies, setQuickReplies] = useState([
    "What is your MOQ?", "Do you offer free samples?", "Shipping time?", "Request a quote",
  ]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetchChatbotConfig()
      .then((cfg) => {
        if (cfg.welcome) setWelcome(cfg.welcome);
        if (cfg.quickReplies?.length) setQuickReplies(cfg.quickReplies);
        setMessages([{ from: "bot", text: cfg.welcome || welcome }]);
      })
      .catch(() => setMessages([{ from: "bot", text: welcome }]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setMessages((m) => [...m, { from: "user", text: msg }]);
    setInput("");
    setLoading(true);
    try {
      const res = await sendChat(msg, "web");
      setMessages((m) => [...m, { from: "bot", text: res.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { from: "bot", text: "Sorry, something went wrong. Please email sales@bestformedals.com." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Toggle button */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full bg-[#C9A227] hover:bg-[#A6841B] text-white shadow-xl flex items-center justify-center transition-transform hover:scale-105"
        aria-label="Chat"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed bottom-24 right-6 z-[60] w-[92vw] max-w-[380px] bg-white rounded-2xl shadow-2xl border border-neutral-100 overflow-hidden animate-fade-up">
          <div className="bg-[#14171c] text-white px-5 py-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#C9A227] flex items-center justify-center">
              <Bot className="w-5 h-5" />
            </div>
            <div>
              <div className="font-display font-bold text-sm">Best For Medals Assistant</div>
              <div className="text-[11px] text-[#E7C766]">Typically replies instantly</div>
            </div>
          </div>

          <div ref={scrollRef} className="h-80 overflow-y-auto p-4 space-y-3 bg-neutral-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    m.from === "user"
                      ? "bg-[#C9A227] text-white rounded-br-sm"
                      : "bg-white text-neutral-700 border border-neutral-100 rounded-bl-sm"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-neutral-100 rounded-2xl rounded-bl-sm px-4 py-3">
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="px-4 pt-2 flex flex-wrap gap-2 bg-neutral-50">
            {quickReplies.map((q) => (
              <button
                key={q}
                onClick={() => send(q)}
                className="text-xs px-3 py-1.5 rounded-full border border-neutral-200 bg-white text-neutral-600 hover:border-[#C9A227] hover:text-[#C9A227] transition-colors"
              >
                {q}
              </button>
            ))}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="p-3 bg-neutral-50 flex items-center gap-2"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 px-4 py-2.5 rounded-full border border-neutral-200 text-sm focus:outline-none focus:border-[#C9A227]"
            />
            <button type="submit" className="w-10 h-10 rounded-full bg-[#C9A227] hover:bg-[#A6841B] text-white flex items-center justify-center">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default Chatbot;
